import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './Transactions.css';
import TransactionTypeModal from './TransactionTypeModal';

function Transactions({ triggerAddTransaction, onTransactionFormOpen }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Filters & Grouping
  const [filterType, setFilterType] = useState('all');
  const [filterPaid, setFilterPaid] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [groupBy, setGroupBy] = useState('none');
  const [categories, setCategories] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const user = auth.currentUser;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];


  // Handle trigger from Dashboard dropdown
  useEffect(() => {
    if (triggerAddTransaction) {
      setShowAddModal(true);
      if (onTransactionFormOpen) {
        onTransactionFormOpen();
      }
    }
  }, [triggerAddTransaction, onTransactionFormOpen]);

  // Load transactions for selected month
  useEffect(() => {
    if (!user) return;
    
    setLoading(true);
    const monthStr = String(selectedMonth + 1).padStart(2, '0');
    const monthKey = `${selectedYear}-${monthStr}`;
    
    const loadTransactions = async () => {
      try {
        const transactionsRef = collection(db, 'users', user.uid, 'transactions', monthKey, 'items');
        const snapshot = await getDocs(transactionsRef);
        
        const transactionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by date descending
        transactionsData.sort((a, b) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return b.date.localeCompare(a.date);
        });
        
        setTransactions(transactionsData);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(transactionsData.map(t => t.category).filter(c => c))].sort();
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading transactions:', error);
        setLoading(false);
      }
    };
    
    loadTransactions();
  }, [user, selectedYear, selectedMonth]);

  // Apply filters
  useEffect(() => {
    let filtered = [...transactions];
    
    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.type?.toLowerCase() === filterType.toLowerCase());
    }
    
    // Paid filter
    if (filterPaid !== 'all') {
      const isPaid = filterPaid === 'paid';
      filtered = filtered.filter(t => t.paid === isPaid);
    }
    
    // Category filter (multi-select)
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(t => selectedCategories.includes(t.category));
    }
    
    setFilteredTransactions(filtered);
  }, [transactions, filterType, filterPaid, selectedCategories]);

  // Auto-expand all groups when groupBy changes
  useEffect(() => {
    if (groupBy !== 'none') {
      const groups = getGroupedTransactions();
      const allExpanded = {};
      Object.keys(groups).forEach(key => {
        allExpanded[key] = true;
      });
      setExpandedGroups(allExpanded);
    }
  }, [groupBy, filteredTransactions]);

  const resetFilters = () => {
    setFilterType('all');
    setFilterPaid('all');
    setSelectedCategories([]);
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const selectAllCategories = () => {
    setSelectedCategories([...categories]);
  };

  const clearAllCategories = () => {
    setSelectedCategories([]);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const formatAmount = (amount) => {
  const num = parseFloat(amount) || 0;
  const absNum = Math.abs(num);
  
  const formatted = absNum.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  if (num < 0) {
    return `(£${formatted})`;
  }
  return `£${formatted}`;
};

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Input format: DD-MM-YYYY
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    
    const day = parts[0];
    const month = parts[1];
    const year = parts[2];
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month) - 1;
    
    return `${day} ${monthNames[monthIndex]} ${year}`;
  };

  // Group transactions
  const getGroupedTransactions = () => {
    if (groupBy === 'none') {
      return { 'All Transactions': filteredTransactions };
    }

    const grouped = {};
    filteredTransactions.forEach(transaction => {
      let key = '';
      
      if (groupBy === 'type') {
        key = transaction.type || 'Unknown';
      } else if (groupBy === 'category') {
        key = transaction.category || 'Uncategorized';
      } else if (groupBy === 'account') {
        key = transaction.account || 'Unknown Account';
      }
      
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(transaction);
    });

    return grouped;
  };

  const calculateGroupTotal = (groupTransactions) => {
    return groupTransactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
  };

  const formatTotal = (total) => {
  const num = parseFloat(total) || 0;
  const absNum = Math.abs(num);
  
  const formatted = absNum.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  if (num < 0) {
    return `(£${formatted})`;
  }
  return `£${formatted}`;
};

  const groupedData = getGroupedTransactions();

  return (
    <div className="transactions-container">
      {/* Header */}
      <div className="transactions-header">
        <h2>Transactions</h2>
        <div className="header-right">
          <div className="year-selector">
            <button className="year-btn" onClick={() => setSelectedYear(selectedYear - 1)}>◀</button>
            <span className="year-display">{selectedYear}</span>
            <button className="year-btn" onClick={() => setSelectedYear(selectedYear + 1)}>▶</button>
          </div>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>+ Add Transaction</button>
        </div>
      </div>
      
      {/* Month Tabs */}
      <div className="month-tabs">
        {months.map((month, index) => (
          <button
            key={month}
            className={`month-tab ${selectedMonth === index ? 'active' : ''}`}
            onClick={() => setSelectedMonth(index)}
          >
            {month}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
            <option value="transfer">Transfer</option>
            <option value="payment">Payment</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterPaid} onChange={(e) => setFilterPaid(e.target.value)}>
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>
        
        <div className="filter-group category-filter">
          <label>Categories:</label>
          <div className="category-dropdown">
            <button className="category-dropdown-btn">
              {selectedCategories.length === 0 ? 'All Categories' : `${selectedCategories.length} selected`}
            </button>
            <div className="category-dropdown-content">
              <div className="category-actions">
                <button onClick={selectAllCategories}>Select All</button>
                <button onClick={clearAllCategories}>Clear All</button>
              </div>
              {categories.map(cat => (
                <label key={cat} className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat)}
                    onChange={() => toggleCategory(cat)}
                  />
                  <span>{cat}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="filter-group">
          <label>Group By:</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="none">No Grouping</option>
            <option value="type">Type</option>
            <option value="category">Category</option>
            <option value="account">Account</option>
          </select>
        </div>
        
        <button className="btn-reset" onClick={resetFilters}>Reset Filters</button>
        
        <div className="transaction-count">
          Showing {filteredTransactions.length} of {transactions.length} transactions
        </div>
      </div>

      {/* Transactions List */}
      {loading ? (
        <div className="loading-state">Loading transactions...</div>
      ) : filteredTransactions.length === 0 ? (
        <div className="empty-state">
          {transactions.length === 0 ? 'No transactions for this month.' : 'No transactions match your filters.'}
        </div>
      ) : (
        <div className="grouped-tables">
          {Object.keys(groupedData).map((groupName) => {
            const groupTransactions = groupedData[groupName];
            const groupTotal = calculateGroupTotal(groupTransactions);
            const isExpanded = expandedGroups[groupName] !== false;
            
            return (
              <div key={groupName} className="group-section">
                {groupBy !== 'none' && (
                  <div className="group-header" onClick={() => toggleGroup(groupName)} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span>{isExpanded ? '▼' : '▶'}</span>
                      <h3>{groupName}</h3>
                    </div>
                    <span className="group-total">Total: {formatTotal(groupTotal)}</span>
                  </div>
                )}
                
                {isExpanded && (
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>DATE</th>
                        <th>TYPE</th>
                        <th>NAME</th>
                        <th>CATEGORY</th>
                        <th>ACCOUNT</th>
                        <th>PERIOD</th>
                        <th className="right">AMOUNT</th>
                        <th className="center">PAID</th>
                        <th className="center">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupTransactions.map((transaction, index) => (
                        <tr key={`${transaction.id}-${index}`} className={transaction.paid ? '' : 'unpaid'}>
                          <td>{formatDate(transaction.date)}</td>
                          <td>
                            <span className={`type-badge ${transaction.type?.toLowerCase()}`}>
                              {transaction.type}
                            </span>
                          </td>
                          <td><strong>{transaction.name || '-'}</strong></td>
                          <td>{transaction.category || '-'}</td>
                          <td>{transaction.account || '-'}</td>
                          <td>{transaction.period || '-'}</td>
                          <td className={`right amount ${transaction.type?.toLowerCase()}`}>
                            {formatAmount(transaction.amount)}
                          </td>
                          <td className="center">
                            {transaction.paid ? '✅' : '❌'}
                          </td>
                          <td className="center actions">
                            <button className="btn-edit">Edit</button>
                            <button className="btn-delete">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {groupBy !== 'none' && (
                      <tfoot>
                        <tr className="group-totals-row">
                          <td colSpan="6"><strong>{groupName} TOTAL</strong></td>
                          <td className="right">
                            <strong>{formatTotal(groupTotal)}</strong>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                )}
              </div>
            );
          })}
          
          {/* Grand Total */}
          {groupBy !== 'none' && (
            <div className="grand-total">
              <strong>GRAND TOTAL: {formatTotal(filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0))}</strong>
            </div>
          )}
          
          {/* No Grouping Total */}
          {groupBy === 'none' && (
            <div className="grand-total">
              <strong>TOTAL: {formatTotal(filteredTransactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0))}</strong>
            </div>
          )}
        </div>
      )}

      {/* Add Transaction Modal */}
      {showAddModal && (
        <TransactionTypeModal onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}

export default Transactions;